import React, { useEffect, useState } from "react";

// bootstrap
import Row from "react-bootstrap/esm/Row";
import Col from "react-bootstrap/esm/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";

// custom components
import ActionsModal from "_components/molecules/Modals/actions-modal";

// styling
import { Colors, Spacing } from "_styles";

// redux
import { useDispatch, useSelector } from "react-redux";
import { resetEditPasswordForm, deleteEmployee, selectDeleteEmployeeStatus, resetDeleteEmployeeFlags, selectDeleteEmployeeError, selectSelectedEmployeeProfile, selectUpdatedEmployeeProfile, selectUpdateEmployeeStatus, updateEmployee, resetUpdateEmployeeStatus, selectUpdateEmployeeError, resetUpdateEmployeeError } from "_redux/slices/timeline/employees";
import { selectUsedTheme } from '_redux/slices/timeline/theme';

// placeholder
import EditPasswordForm from "./edit-password-form";

// tremor
import { Subtitle } from "@tremor/react";

const EditEmployeeInfo = ({ closeEmployeeModal }) => {

    //#region redux
    const dispatch = useDispatch();

    const selectedEmployeeProfile = useSelector(selectSelectedEmployeeProfile);      // Currently selected employee's profile
    const deleteStatus = useSelector(selectDeleteEmployeeStatus);                    // Status of deletion
    const deleteError = useSelector(selectDeleteEmployeeError);                      // Error thrown during delete
    const updatedEmployeeProfileStatus = useSelector(selectUpdateEmployeeStatus);  // Status of edit
    const updatedEmployeeProfile = useSelector(selectUpdatedEmployeeProfile);          // Updated profile after edit
    const updateEmployeeError = useSelector(selectUpdateEmployeeError)
    const usedTheme = useSelector(selectUsedTheme);
    //#endregion

    //#region local states
    const [enabledField, setEnabledField] = useState("");                            // Which field is currently being edited
    const [employeeInformation, setEmployeeInformation] = useState({ title: "", status: "", email: "", password: "", role: "" }); // Local copy of employee info
    const [showEditPasswordModal, setShowEditPasswordModal] = useState(false);       // Open edit password modal
    const [showDeleteModal, setShowDeleteModal] = useState(false);                   // Open delete employee modal
    const [fieldError, setFieldError] = useState("");                                // Field error
    //#endregion


    //#region useEffect
    // set employee info for selected employee
    useEffect(() => {
        // check if null or empty
        if (selectedEmployeeProfile) {
            // set employee info
            setEmployeeInformation(employeeInfo(selectedEmployeeProfile));
        }
    }, [selectedEmployeeProfile]);

    // set employee info when editing employee
    useEffect(() => {
        if (updatedEmployeeProfileStatus == "succeeded" && !Object.isObjectEmpty(updatedEmployeeProfile)) {
            setEmployeeInformation(employeeInfo(updatedEmployeeProfile));
            setFieldError("");
            setEnabledField("");
            dispatch(resetUpdateEmployeeStatus())
            dispatch(resetUpdateEmployeeError())
        }
        if (updatedEmployeeProfileStatus === "failed") dispatch(resetUpdateEmployeeStatus())
    }, [updatedEmployeeProfileStatus, updatedEmployeeProfile])

    // delete status listener
    useEffect(() => {
        if (deleteStatus === "succeeded") {
            dispatch(resetDeleteEmployeeFlags());
            closeEmployeeModal?.();
        }
    }, [deleteStatus]);

    //#endregion

    //#region functions

    // function that takes in the selected employee profile and returns an object with the employee info
    const employeeInfo = (profile) => ({
        title: profile?.title ?? "",
        status: profile?.status ?? "",
        email: profile?.email ?? "",
        password: "", // never read the stored password; it is no longer persisted
        role: profile?.role ?? "",
        employeeId: profile.employeeId
    });

    // enable field for editing, if password open modal
    const handleEnableField = (fieldName) => {
        if (fieldName == "password") {
            handleShowEditPasswordModal();
        }
        else {
            setEnabledField(fieldName);
        }
    }

    // capitalize the first char in the field name
    const formatFieldName = (fieldName) => {
        // capitalize the first letter of the field name
        if (fieldName) {
            return `${fieldName?.charAt(0)?.toUpperCase()}${fieldName?.slice(1)}` ?? "N/A";
        } else return "N/A"
    };

    // don't allow empty fields
    const handleChangeField = (fieldName, fieldValue) => {
        if (!fieldValue) setFieldError(`${fieldName} can't be empty`)
        if (fieldName !== "password") {
            setEmployeeInformation((prevState) => ({
                ...prevState,
                [fieldName]: fieldValue
            }));
        }
    };

    // save new info and confirm formatting
    const saveEmployeeUpdates = (fieldName) => {
        if (fieldName && selectedEmployeeProfile.userId) {

            if (fieldName === "title") {
                const title = employeeInformation.title.trim();
                if (title.length < 6) {
                    return setFieldError("Title must be at least 6 characters long");
                }
            }

            if (fieldName == "email") {
                const tempEmail = employeeInformation.email && employeeInformation.email.trim();
                let emailRegex = /[a-z0-9]+@[a-z]+\.[a-z]{2,3}/;
                if (!emailRegex.test(tempEmail)) {
                    return setFieldError("Invalid email format")
                }
            };

            if (fieldName === "role") {
                if (!employeeInformation.role || employeeInformation.role === "") {
                    return setFieldError("Please select a role");
                }
            }

            const data = {
                userId: selectedEmployeeProfile.userId,
                body: {
                    [fieldName]: employeeInformation[fieldName].trim()
                }
            }
            dispatch(updateEmployee(data))

        }
    }

    // revert data back to original when cancelling edit
    const cancelEditEmployee = () => {
        setFieldError("");
        setEnabledField("");
        setEmployeeInformation({
            title: selectedEmployeeProfile?.title ?? "",
            status: selectedEmployeeProfile?.status ?? "",
            email: selectedEmployeeProfile?.email ?? "",
            password: "", // never read the stored password; it is no longer persisted
            role: selectedEmployeeProfile?.role ?? "",
            employeeId: selectedEmployeeProfile?.employeeId ?? "",

        });
    }

    // modal open handler
    const handleShowEditPasswordModal = () => {
        setShowEditPasswordModal(true); // show modal
    };

    // modal close handler
    const handleCloseEditPasswordModal = () => {
        dispatch(resetEditPasswordForm()); // reset form for next use
        setShowEditPasswordModal(false); // close modal
        setEnabledField("");
    };

    // Handlers to open or close the delete confirmation modal
    const openDeleteModal = () => setShowDeleteModal(true);

    const closeDeleteModal = () => {
        setShowDeleteModal(false)
        dispatch(resetDeleteEmployeeFlags())
    };

    // Handler for confirming the deletion
    const handleDeleteConfirm = () => {
        // Assuming you dispatch an action to delete the employee
        dispatch(deleteEmployee(selectedEmployeeProfile));
    };
    //#endregion

    return (
        <>

            <Row>
                {/* Title & subtitle container */}
                <Col xs={12} className="mx-auto">

                    {/* Editable Employee Fields */}
                    <Row>
                        {["title", "status", "email", "password", "role"].map((field) => (
                            <Col xs={12} md={6} lg={4} className="mt-4" key={field}>

                                {/* Label */}
                                <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>
                                    {formatFieldName(field)}
                                </Form.Label>

                                {/* Field Input */}
                                {field === "role" ? (
                                    <Form.Select
                                        aria-label="Role select"
                                        onChange={(e) => handleChangeField(field, e.target.value)}
                                        disabled={enabledField !== field}
                                        value={employeeInformation[field]}
                                        style={{ borderColor: usedTheme.BORDER_COLOR, backgroundColor: usedTheme.BACKGROUND_COLOR, color: usedTheme.TEXT_COLOR }}
                                    >
                                        <option value="">Select a role</option>
                                        <option value="manager">Manager</option>
                                        <option value="staff">Staff</option>
                                        <option value="driver">Driver</option>
                                    </Form.Select>
                                ) : field === "status" ? (
                                    <Form.Select
                                        aria-label="Status select"
                                        onChange={(e) => handleChangeField(field, e.target.value)}
                                        disabled={enabledField !== field}
                                        value={employeeInformation[field]}
                                        style={{ borderColor: usedTheme.BORDER_COLOR, backgroundColor: usedTheme.BACKGROUND_COLOR, color: usedTheme.TEXT_COLOR }}
                                    >
                                        <option value="online">Online</option>
                                        <option value="offline">Offline</option>
                                    </Form.Select>
                                ) : (
                                    <Form.Control
                                        onChange={(e) => handleChangeField(field, e.target.value)}
                                        type={field === "password" ? "password" : "text"}
                                        disabled={field === "password" ? true : enabledField !== field}
                                        value={employeeInformation[field]}
                                        style={{ borderColor: usedTheme.BORDER_COLOR, backgroundColor: usedTheme.BACKGROUND_COLOR, color: usedTheme.TEXT_COLOR }}
                                    />
                                )}

                                {/* Error Message */}
                                {fieldError && enabledField === field && (
                                    <p style={{ color: "red", margin: 0 }}>{fieldError}</p>
                                )}

                                {/* Edit / Save Buttons */}
                                {enabledField === field ? (
                                    <div className="d-flex gap-2">
                                        <Button
                                            onClick={() => cancelEditEmployee("")}
                                            variant="outline-danger"
                                            style={{ width: "175px" }}
                                            className="mt-3"
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            onClick={() => saveEmployeeUpdates(field)}
                                            variant="outline-success"
                                            style={{ width: "175px" }}
                                            className="mt-3"

                                        >
                                            {updatedEmployeeProfileStatus === "loading" ? (
                                                <Spinner size="sm" />
                                            ) : (
                                                "Save"
                                            )}
                                        </Button>
                                    </div>
                                ) : (
                                    <Button
                                        onClick={() => handleEnableField(field)}
                                        variant="outline-primary"
                                        // disabled={field === "employeeId"}
                                        style={{ width: "175px" }}
                                        disabled={field === "employeeId" || field === "status"}
                                        className="mt-3"
                                    >
                                        Edit
                                    </Button>
                                )}
                            </Col>
                        ))}
                    </Row>
                    <Row>
                        {updateEmployeeError ?
                            <Col xs={3} md={12}>
                                <p className="text-danger">{updateEmployeeError}</p>
                            </Col> : <></>

                        }
                    </Row>

                    {/* Delete Employee Button */}
                    <Row className="mt-5">
                        {/* Block User Button */}
                        <Col xs={3}>
                            <Button onClick={openDeleteModal} variant="danger" style={{ whiteSpace: "nowrap" }}>
                                {deleteStatus === "loading" ? "loading" : "Delete Employee"}
                            </Button>
                        </Col>

                        {/* Delete Employee modal */}
                        <ActionsModal show={showDeleteModal}
                            handleClose={closeDeleteModal}
                            title={"Delete Employee"}
                            // subtitle={Object.isObjectEmpty(selectedTimeLine) ? `To create an area in your airport fill in the below form and add any airport locations that are within the airport area and click confirm` : `To edit an airport area update the information below and click confirm`}
                            minWidth="445px"
                            maxWidth="445px"
                            isTimeline={true}
                        >
                            {/* Delete Confirmation Modal */}
                            <Subtitle>{`${employeeInformation?.title} will be deleted. Are you sure you want to continue ?`}</Subtitle>
                            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "20px" }} >
                                <Button onClick={closeDeleteModal} variant={"danger"} style={{ marginRight: Spacing.SCALE_8 }}>
                                    Cancel
                                </Button>
                                <Button onClick={handleDeleteConfirm} variant={"primary"}>
                                    {deleteStatus === "loading" ? <Spinner animation="border" size="sm" /> : "Confirm"}
                                </Button>
                            </div>
                            {deleteError &&
                                <Form.Label style={{ color: "red" }}>
                                    {deleteError}
                                </Form.Label>
                            }
                        </ActionsModal>
                    </Row>

                    {/* Edit Password Modal */}
                    <ActionsModal
                        show={showEditPasswordModal}
                        handleClose={handleCloseEditPasswordModal}
                        title="Edit Employee Password"
                        minWidth="445px"
                        maxWidth="445px"
                        isTimeline={true}
                    >
                        <EditPasswordForm
                            closeModal={handleCloseEditPasswordModal}
                            setEmployeeInformation={setEmployeeInformation}
                            employee={selectedEmployeeProfile}
                        />
                    </ActionsModal>
                </Col>
            </Row>
        </>
    );
};

export default EditEmployeeInfo;
